import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import PersonaCheckbox from './PersonaCheckbox';
import React from 'react';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import strings from 'owa-strings/owa-mini-strings';
import { FolderLabel } from './FolderLabel';
import { getAriaSummaryLabel } from '../utils/getAriaSummaryLabel';
import { getFilteredMessageClassIcon } from '../utils/getFilteredMessageClassIcon';
import { getPropertyIcons } from '../utils/getPropertyIcons';
import { highlightTermsInHtmlElement } from 'dom-utils/lib/utils/highlightUtils';
import { MailListItemIconBar } from './MailListItemIconBar';
import { MailListItemTimeStamp } from './MailListItemTimeStamp';
import { PropertyIcons } from '../schema/PropertyIcons';
import { RichContents } from '../schema/RichContents';

const styles = require('./MailListItem.scss');
const classNames = require('classnames/bind').bind(styles);

const DATA_TEST_ID = 'MailListItem';

/**
 * These styles are static, hence moving them out
 */
const bundleNameStylesFirstLine = classNames(styles['mailListItem-firstLine'], 'ellipisOnOverflow');

const bundleNameStylesSecondLine = classNames(
    styles['mailListItem-secondLine'],
    'ellipisOnOverflow'
);

const bundleNameStylesPreview = classNames(styles['mailListItem-preview'], 'ellipisOnOverflow');

type SupportedItemCardClassNames =
    | 'mailListItemCard'
    | 'mailListItemCard-withoutRsvp'
    | 'mailListItemCard-withoutPreview'
    | 'mailListItemCard-withoutPreview-withoutRsvp';

export interface MailListItemProps {
    effectiveMentioned: boolean;
    firstLineText: string;
    hasAttachment: boolean;
    importance: ImportanceType;
    isFlagged: boolean;
    isMeetingMessage: boolean;
    isPinned: boolean;
    isSelected: boolean;
    messageClassIcon: PropertyIcons;
    lastDeliveryTimestamp: string;
    likeCount: number;
    rowId: string;
    secondLineText: string;
    showDraftsIndicator: boolean;
    thirdLineText: string;
    unreadCount: number;
    lastSender: SingleRecipientType;
    isInCheckedMode: boolean;
    shouldShowAvatar: boolean;
    shouldShowFolderLabel?: boolean;
    parentFolderName?: string;
    termsToHighlight?: string[];
    onMultiSelect?: (rowId: string) => void;
    richContents?: RichContents;
    isSearchResult?: boolean;
}

export const MailListItem = React.forwardRef(
    (props: MailListItemProps, ref?: React.RefObject<HTMLElement>) => {
        const {
            rowId,
            unreadCount,
            isFlagged,
            isPinned,
            isSelected,
            messageClassIcon,
            firstLineText,
            secondLineText,
            thirdLineText,
            lastSender,
            showDraftsIndicator,
            lastDeliveryTimestamp,
            isInCheckedMode,
            shouldShowAvatar,
            shouldShowFolderLabel = false,
            parentFolderName,
            termsToHighlight,
            onMultiSelect,
            richContents,
            isSearchResult = false,
        } = props;

        // needed for mark or un-mark at every render call
        const highlightTermsWrapper = (element: HTMLElement) => {
            if (element && termsToHighlight?.length > 0) {
                highlightTermsInHtmlElement(element, termsToHighlight);
            }
        };

        const isUnread: boolean = unreadCount > 0;
        const isPersonaCheckboxNeeded = isInCheckedMode || shouldShowAvatar;

        const showRSVPUnit = !!richContents?.RSVPUnit?.Component;
        const filteredMessageClassIcon = getFilteredMessageClassIcon(
            showRSVPUnit,
            messageClassIcon
        );

        const outerContainerClassNames = classNames(
            {
                'mailListItem--unread': isUnread,
                'mailListItem--selected': isSelected,
                'mailListItem--showLeftContainer': isPersonaCheckboxNeeded,
                'mailListItem--noLeftContainer': !isPersonaCheckboxNeeded,
                'mailListItem--search': isSearchResult,
            },
            styles.mailListItemAdditionalStyle,
            styles.mailListItem,
            styles.mailListItemWithRSVP,
            calculateBackgroundColorClass(isFlagged, isPinned, isSelected),
            getMailListItemCardClassName(!!thirdLineText, showRSVPUnit)
        );
        const pressedStateBehavior = {
            onTouchStart: () => {
                ref?.current?.classList?.add(styles.mailListItemPressed);
            },
            onTouchEnd: () => {
                ref?.current?.classList?.remove(styles.mailListItemPressed);
            },
        };

        const propertyIcons: PropertyIcons[] = getPropertyIcons(props);

        const containerAriaLabel = getAriaSummaryLabel(props);

        return (
            <article
                ref={ref}
                className={outerContainerClassNames}
                {...pressedStateBehavior}
                data-testid={DATA_TEST_ID}>
                <div className={styles.mailListItemContainerWithPersona}>
                    {isUnread && (
                        <div
                            className={classNames(
                                styles['mailListItem-unreadIndicator'],
                                !isPersonaCheckboxNeeded &&
                                    styles['mailListItem-noAvatar-unreadIndicator'],
                                isInCheckedMode &&
                                    styles['mailListItem-multiSelect-unreadIndicator']
                            )}></div>
                    )}
                    {isPersonaCheckboxNeeded && (
                        <PersonaCheckbox
                            rowId={rowId}
                            isSelected={isSelected}
                            isInCheckedMode={isInCheckedMode}
                            lastSender={lastSender}
                            onMultiSelect={onMultiSelect}
                        />
                    )}
                    <div
                        className={styles['mailListItem-container']}
                        tabIndex={0}
                        role="button"
                        aria-label={containerAriaLabel}>
                        <div className={styles['mailListItem-content-firstLine']}>
                            <div className={bundleNameStylesFirstLine} ref={highlightTermsWrapper}>
                                {showDraftsIndicator && (
                                    <span className={styles.draftPlaceHolderText}>
                                        {strings.draftSenderPersonaPlaceholder}
                                    </span>
                                )}
                                {firstLineText}
                            </div>
                            <MailListItemIconBar
                                className={styles['mailListItem--iconBar']}
                                isFlagged={isFlagged}
                                isPinned={isPinned}
                                unreadCount={unreadCount}
                                propertyIcons={propertyIcons}
                                messageClassIcon={filteredMessageClassIcon}
                            />
                        </div>
                        <div className={styles['mailListItem-content-secondLine']}>
                            <h2 className={bundleNameStylesSecondLine} ref={highlightTermsWrapper}>
                                {secondLineText}
                            </h2>
                            <MailListItemTimeStamp
                                className={styles['mailListItem-timestamp']}
                                timeString={lastDeliveryTimestamp}
                            />
                        </div>
                        {thirdLineText && (
                            <div className={styles['mailListItem-content-thirdLine']}>
                                <div
                                    className={bundleNameStylesPreview}
                                    ref={highlightTermsWrapper}>
                                    {thirdLineText}
                                </div>
                                {unreadCount > 1 && (
                                    <div className={styles['mailListItem-additionalPill']}>
                                        {unreadCount}
                                    </div>
                                )}
                                {shouldShowFolderLabel && (
                                    <FolderLabel
                                        folderName={parentFolderName}
                                        isUnread={isUnread}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {richContents?.RSVPUnit?.Component && (
                    <div
                        className={classNames(
                            styles.RsvpUnit,
                            isPersonaCheckboxNeeded && styles.showAvatar
                        )}>
                        {richContents.RSVPUnit.Component}
                    </div>
                )}
            </article>
        );
    }
);

const calculateBackgroundColorClass = (
    isFlagged: boolean,
    isPinned: boolean,
    isSelected: boolean
): string => {
    if (isSelected) {
        return styles.selectedMailListItemColor;
    } else if (isFlagged) {
        return styles.flaggedMailListItemColor;
    } else if (isPinned) {
        return styles.pinnedMailListItemColor;
    }

    return styles.regularMailListItemColor;
};

const getMailListItemCardClassName = (
    showPreviewText: boolean,
    showRSVPUnit: boolean
): SupportedItemCardClassNames => {
    if (showPreviewText && showRSVPUnit) {
        return 'mailListItemCard';
    }
    if (showPreviewText) {
        return 'mailListItemCard-withoutRsvp';
    }
    if (showRSVPUnit) {
        return 'mailListItemCard-withoutPreview';
    }
    return 'mailListItemCard-withoutPreview-withoutRsvp';
};
